import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { MetadataContext } from '@Globals/Metadata'
import { CheckoutV2PaymentMethod } from '@Pages/onestepcheckout'

interface BraintreeInstance {
  applePay: { VERSION: string, create: Function }
  client: { VERSION: string, create: Function }
  dataCollector: { VERSION: string, create: Function }
  hostedFields: { VERSION: string, create: Function, supportsInputFormatting: Function }
  paypalCheckout: { VERSION: string, create: Function, isSupported: Function }
  venmo: { VERSION: string, create: Function, isBrowserSupported: Function }
}

export interface BraintreeContextProps {
  authorization: string | null
  braintreeClient: unknown | null
  braintreeInstance: BraintreeInstance | null
  braintreeLoaded: boolean
}

export const BraintreeContext = createContext<BraintreeContextProps>({
  authorization: null,
  braintreeClient: null,
  braintreeInstance: null,
  braintreeLoaded: true,
})

export interface BraintreeConfig {
  createClientToken?: {
    clientToken?: string
  }
}

interface BrainTreeContextProviderV2Props {
  braintreeConfig?: BraintreeConfig
  children: React.JSX.Element
  paymentOptions: CheckoutV2PaymentMethod[]
}

export const BrainTreeContextProviderV2: FC<BrainTreeContextProviderV2Props> = ({
  braintreeConfig,
  children,
  paymentOptions,
}) => {
  const { store } = useContext(MetadataContext)
  const [authorization, setAuthorization] = useState<string | null>(null)
  const [braintreeInstance, setBraintreeInstance] = useState<BraintreeInstance | null>(null)
  const [braintreeClient, setBraintreeClient] = useState(null)
  const [braintreeScriptState, setBraintreeScriptState] = useState({})
  const [braintreeLoaded, setBraintreeLoaded] = useState(false)

  const createScriptForType = useCallback((key: string, url: string) => {
    if (isEmpty(braintreeConfig)) {
      return
    }
    setBraintreeScriptState(prev => ({
      ...prev,
      [key]: false,
    }))
    const head: HTMLHeadElement | null = document.querySelector('head')
    const script = document.createElement('script')
    script.setAttribute('src', url)
    script.setAttribute('crossorigin', 'anonymous')
    if (head) {
      head.appendChild(script)
    }
    script.onload = () => setBraintreeScriptState(prev => ({
      ...prev,
      [key]: true,
    }))
  }, [braintreeConfig])

  const createBraintreeSession = useCallback(async () => {
    if (!braintreeConfig || !['27', '36'].includes(store.id)) {
      return
    }
    if (!braintreeConfig.createClientToken?.clientToken) {
      setBraintreeLoaded(false)
      return
    }
    setAuthorization(braintreeConfig.createClientToken.clientToken)
    createScriptForType('client', 'https://js.braintreegateway.com/web/3.88.4/js/client.min.js')
    const types: string[] = compact(paymentOptions.filter(option => option.provider === 'braintree')
      .map(option => option.type))
    if (types.includes('credit_card')) {
      createScriptForType('hostedFields', 'https://js.braintreegateway.com/web/3.88.4/js/hosted-fields.min.js')
    }
    if (types.includes('paypal')) {
      createScriptForType('paypal', 'https://js.braintreegateway.com/web/3.88.4/js/paypal-checkout.min.js')
    }
    const isApplePayAvailable = typeof window !== 'undefined' && 'ApplePaySession' in window
    if (types.includes('applepay') && isApplePayAvailable) {
      createScriptForType('applepay', 'https://js.braintreegateway.com/web/3.88.4/js/apple-pay.min.js')
    }
    if (types.includes('venmo')) {
      createScriptForType('venmo', 'https://js.braintreegateway.com/web/3.88.4/js/venmo.min.js')
      createScriptForType('dataCollector', 'https://js.braintreegateway.com/web/3.88.4/js/data-collector.min.js')
    }
  }, [braintreeConfig, createScriptForType, paymentOptions, store.id])

  useEffect(() => {
    if (isEmpty(paymentOptions)) {
      return
    }
    const keys = Object.values(braintreeScriptState)
      .every(loaded => loaded === true)
    setBraintreeLoaded(keys)
    if (braintreeLoaded && !braintreeInstance && (window as any).braintree) {
      const newBraintreeInstance = (window as any).braintree
      setBraintreeInstance(newBraintreeInstance)
      newBraintreeInstance.client.create({ authorization, ...({}) }, (clientErr, clientInstance) => {
        if (clientErr) {
          console.error('Error creating Braintree client:', clientErr)
        } else {
          setBraintreeClient(clientInstance)
        }
      })
    }
  }, [authorization, braintreeInstance, braintreeLoaded, braintreeScriptState, paymentOptions])

  useEffect(() => {
    createBraintreeSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(() => ({
    authorization,
    braintreeInstance,
    braintreeLoaded,
    braintreeClient,
  }), [
    authorization,
    braintreeInstance,
    braintreeLoaded,
    braintreeClient,
  ])
  return (
    <BraintreeContext.Provider value={value}>
      {children}
    </BraintreeContext.Provider>
  )
}
